// Privacy — what the site keeps, where, and why, and what a player can do
// about it.
//
// Written in the plainest words that are still accurate, because the point of
// the page is that somebody reads it. **It must never say more than the code
// does**: every claim here is checked in CLAUDE.md (the data layer, step 4) —
// no cookies, no requests to anyone but Cloudflare, Supabase and TMDB, no IP
// address stored with a result, the 12-month id removal, the switch and the
// delete button.
import { useState } from 'react';
import { deviceId } from '../lib/device.ts';
import { deleteMyResults, setSharingResults, sharingResults } from '../lib/history.ts';
import './Info.css';

export function Privacy() {
  return (
    <main className="column info">
      <h1 className="info-title">Privacy</h1>

      <p className="info-line">
        Cinemore is a quiz. It has no accounts or sign-in at the moment, no advertising, and
        nothing that follows you around the web. It counts visits with Cloudflare Web Analytics,
        which uses no cookies (see below). This page says exactly what is kept, and where. If
        accounts are ever added, this page will be updated first.
      </p>

      <section className="info-section">
        <h2 className="heading info-heading">Who runs Cinemore</h2>
        <p className="info-line">
          Cinemore is run by Matt Mitchell. You can reach me
          at <a href="mailto:Cinemorehq@gmail.com">Cinemorehq@gmail.com</a>. If you're unhappy with how your data is
          handled, you can complain to the Information Commissioner's Office
          at <a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.
        </p>
      </section>

      <section className="info-section">
        <h2 className="heading info-heading">On your device</h2>
        <p className="info-line">Your browser keeps:</p>
        <ul className="info-list">
          <li>
            a random ID for this browser, made the first time you play. It isn't linked to your
            name, your email or anything else about you.
          </li>
          <li>
            the puzzles you've finished and how each one went, which is where your played, won,
            win rate and streak come from
          </li>
          <li>a puzzle you've started but not finished, so you can pick it up again</li>
          <li>whether you've chosen to keep your results out of the site's statistics</li>
        </ul>
        <p className="info-notice">
          All of it is in your browser's own storage. Cinemore doesn't use cookies. Clearing your
          browser's site data removes everything, and you start again as a new player. A phone
          and a laptop are two separate players, and so is a private browsing window.
        </p>
      </section>

      <section className="info-section">
        <h2 className="heading info-heading">What we record</h2>
        <p className="info-line">
          When you finish a puzzle we record the result against your random ID, so the site can
          show how everyone did — the share of players who solved it on each guess, and which
          titles people guess. Each record holds the puzzle, your guesses, which guess you solved
          it on (or that you missed), the time, and the random ID. Nothing else: no name, no
          email, no location, and no IP address.
        </p>
        <p className="info-line info-gap">
          We do this because we have a legitimate interest in running the game and showing how
          players did.
        </p>
        <p className="info-line info-gap">
          After 12 months the random ID is removed from each record. What is left still counts
          toward the totals, but can't be connected to any browser.
        </p>
        <p className="info-line info-gap">
          The records are stored by Supabase on servers in London. A backup copy, without the
          random IDs, is kept privately on GitHub.
        </p>
      </section>

      <PrivacyControls />

      <section className="info-section">
        <h2 className="heading info-heading">Other services your browser talks to</h2>
        <ul className="info-list">
          <li>
            Supabase, which holds the puzzles and the results. Your browser sends your guesses to
            it directly, and gets each puzzle back from it.
          </li>
          <li>
            <a href="https://www.themoviedb.org" target="_blank" rel="noreferrer">TMDB</a> (The
            Movie Database), for posters and actor photos, which load straight from its servers.
          </li>
          <li>Cloudflare, which hosts the site and handles every visit to it.</li>
          <li>
            Cloudflare Web Analytics, which counts visits so we can see how many people use the
            site: which pages are viewed, how quickly they load, and the type of browser and device
            and the country they come from, all as totals. It sets no cookies, doesn't identify
            you, and doesn't follow you to other sites.
          </li>
        </ul>
        <p className="info-notice">
          Like any website, these services see your IP address when your browser connects to
          them. We don't store it with your results. Supabase keeps its own connection logs,
          which include IP addresses, for about a day. Some of these companies are based in the
          US.
        </p>
      </section>

      <section className="info-section">
        <h2 className="heading info-heading">Questions</h2>
        <p className="info-line">
          Anything here that is unclear — the <a href="/contact">contact page</a> has an address.
        </p>
      </section>
    </main>
  );
}

/**
 * The two things a player can do here: keep their results out of the site's
 * statistics, and delete what has already been recorded. Both act on this
 * browser only, because this browser's random ID is the only thing that ties a
 * result to anybody.
 */
function PrivacyControls() {
  const [sharing, setSharing] = useState(sharingResults);
  const [id, setId] = useState(deviceId);
  const [stage, setStage] = useState<'idle' | 'confirm' | 'working' | 'done' | 'failed'>('idle');
  const [removed, setRemoved] = useState(0);

  function toggle(on: boolean) {
    setSharingResults(on);
    setSharing(sharingResults());
  }

  async function remove() {
    setStage('working');
    try {
      setRemoved(await deleteMyResults());
      setId(deviceId());
      setStage('done');
    } catch {
      setStage('failed');
    }
  }

  return (
    <section className="info-section">
      <h2 className="heading info-heading">Your results</h2>
      <div className="panel privacy-panel">
        <label className="privacy-switch">
          <input type="checkbox" checked={sharing} onChange={(e) => toggle(e.target.checked)} />
          <span className="privacy-switch-track" aria-hidden="true" />
          <span className="privacy-switch-label">Include my results in site statistics</span>
        </label>
        <p className="info-notice">
          When this is off, your results stay on this device only: your own stats and archive
          still work, and nothing about your games is recorded on our server. Turning it off
          doesn't remove results already recorded — the button below does that.
        </p>

        <p className="privacy-id">
          <span className="privacy-id-label">This browser's ID</span>
          <code className="privacy-id-value">{id}</code>
        </p>

        {stage === 'confirm' ? (
          <div className="privacy-confirm">
            <p className="info-notice">
              Delete every result recorded against this ID, and clear everything Cinemore has
              saved on this device? This can't be undone.
            </p>
            <div className="privacy-buttons">
              <button type="button" className="btn btn-primary" onClick={remove}>Delete</button>
              <button type="button" className="btn" onClick={() => setStage('idle')}>Cancel</button>
            </div>
          </div>
        ) : (
          <button type="button" className="btn privacy-delete" disabled={stage === 'working'}
                  onClick={() => setStage('confirm')}>
            {stage === 'working' ? 'Deleting…' : 'Delete my results'}
          </button>
        )}
        <p className="info-notice" role="status">
          {stage === 'done'
            ? `Done. ${removed === 1 ? '1 result was' : `${removed} results were`} deleted from our server, and this device has been cleared. Anything you play from now on starts under the new ID above.`
            : stage === 'failed'
              ? "Couldn't reach our server, so nothing was deleted. Check your connection and try again."
              : 'Deletes every result recorded against this ID on our server, and clears everything Cinemore has saved on this device.'}
        </p>
        <p className="info-notice">
          You can also ask us for a copy of what is recorded against your ID through
          the <a href="/contact">contact page</a>.
        </p>
      </div>
    </section>
  );
}
